import Tippy from '@tippyjs/react/headless';
import _isNil from 'lodash/isNil';
import {
  ForwardedRef,
  ReactElement,
  ReactNode,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { IoEllipsisVerticalOutline } from 'react-icons/io5';
import type { Instance, Placement } from 'tippy.js';

import { IconButton } from '../Button/IconButton';

export type Trigger = 'hover' | 'click' | 'none';

export type PopoverProps = {
  children: ReactNode | string;
  launcher?: ReactElement;
  placement?: Placement;
  arrow?: string | boolean | SVGElement | DocumentFragment | undefined;
  trigger?: Trigger;
  disabled?: boolean;
  appendTo?: any;
  setIsTippyVisible?: (visible: boolean) => void;
};

export type PopoverMethods = {
  show: () => void;
  hide: () => void;
  toggle: () => void;
  getTippyState: () => any;
};

function getTrigger(trigger: Trigger) {
  switch (trigger) {
    case 'hover':
      return 'mouseenter focus click';
    case 'click':
      return 'click';
    default:
      return 'manual';
  }
}

export const Popover = forwardRef(
  (
    {
      launcher,
      children,
      placement = 'bottom-end',
      trigger = 'hover',
      arrow = false,
      disabled = false,
      appendTo,
      setIsTippyVisible,
      ...rest
    }: PopoverProps,
    ref: ForwardedRef<PopoverMethods>
  ) => {
    const tippy = useRef<Instance>();
    const [shouldRender, setShouldRender] = useState(false);

    const show = useCallback(() => tippy.current?.show(), []);
    const hide = useCallback(() => tippy.current?.hide(), []);
    const toggle = useCallback(() => {
      return tippy.current?.[tippy.current?.state.isShown ? 'hide' : 'show']();
    }, []);

    const getTippyState = useCallback(() => tippy.current?.state.isVisible, []);
    
    // Force update position when children change
    useEffect(() => {
      if (tippy.current?.state.isVisible) {
        setTimeout(() => {
          tippy.current?.popperInstance?.update();
        }, 0);
      }
    }, [children]);

    useImperativeHandle(
      ref,
      () => ({
        show,
        hide,
        toggle,
        getTippyState,
      }),
      [show, hide, toggle, getTippyState]
    );

    useEffect(() => {
      if (typeof setIsTippyVisible === 'function' && !_isNil(tippy.current)) {
        setIsTippyVisible(!!tippy.current?.state.isVisible);
      }
    }, [tippy.current?.state.isVisible]);

    const launcherButton = (
      <IconButton border padding>
        <IoEllipsisVerticalOutline size={24} />
      </IconButton>
    );

    return (
      <Tippy
        onShow={() => setShouldRender(true)}
        onHide={() => setShouldRender(false)}
        onCreate={(instance) => (tippy.current = instance)}
        onDestroy={() => (tippy.current = undefined)}
        onMount={(instance) => {
          // Force update position on mount
          instance.popperInstance?.update();
        }}
        render={() => (
          shouldRender ? children : null
        )}
        placement={placement}
        arrow={arrow}
        trigger={getTrigger(trigger)}
        interactive
        allowHTML={true}
        disabled={disabled}
        appendTo={appendTo ?? 'parent'}
        popperOptions={{
          ...(!_isNil(appendTo) ? { strategy: 'fixed' } : {}),
          modifiers: [
            {
              name: 'preventOverflow',
              options: {
                padding: 8,
              },
            },
            {
              name: 'flip',
              options: {
                fallbackPlacements: ['top', 'bottom', 'left', 'right'],
              },
            },
          ],
        }}
        duration={[100, 0]}
        {...rest}
      >
        {launcher ?? launcherButton}
      </Tippy>
    );
  }
);

Popover.displayName = 'Popover';